// ============================================================
// ALBATROS — MovementsView.tsx
// Historial de transacciones del día
// ============================================================

import React from 'react';
import {
  ShoppingCart,
  RotateCcw,
  FileText,
  FileSearch,
  FilePlus,
  Eye,
  Ticket,
  History
} from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { Transaction } from '../../types/transaction.types';
import { Skeleton } from '../common/Skeleton';

interface MovementsViewProps {
  history: Transaction[];
  isLoading?: boolean;
  onReprint: (tx: Transaction) => void;
  onInvoice: (tx: Transaction) => void;
  /** Abre el ticket en pantalla, tal como saldra impreso. */
  onViewTicket: (tx: Transaction) => void;
  /** Abre la factura en pantalla, tal como saldra impresa. */
  onViewInvoice: (tx: Transaction) => void;
  /** Convierte un ticket sin factura en factura (pide el cliente). */
  onMakeInvoice: (tx: Transaction) => void;
  onReturn: (tx: Transaction) => void;
}


const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  marcial_card: 'Tarjeta M. Pons',
  gift_card: 'Tarjeta o Cheque Regalo',
  invoice: 'Contra Factura',
};

export default function MovementsView({ history, isLoading, onReprint, onInvoice, onViewTicket, onViewInvoice, onMakeInvoice, onReturn }: MovementsViewProps) {
  const netTotal = history.reduce(
    (acc, tx) => acc + (tx.type === 'sale' ? tx.total : -tx.total),
    0
  );

  return (
    <div className="al-movements al-scrollbar-hide">

      {/* Header */}
      <div className="al-movements-header">
        <div>
          <h2 className="al-movements-title">Movimientos</h2>
          <p className="al-movements-subtitle">
            Historial de terminal {new Date().toLocaleDateString()}
          </p>
        </div>
        <div className="al-movements-net">
          <span className="al-movements-net-label">Neto Hoy</span>
          <span className="al-movements-net-value">{formatCurrency(netTotal)}</span>
        </div>
      </div>

      {/* Transaction list */}
      <div>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={`skel-${i}`} className="al-tx-row" style={{ opacity: 0.7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <Skeleton width="2.5rem" height="2.5rem" radius="50%" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  <Skeleton width="8rem" height="1rem" />
                  <Skeleton width="12rem" height="0.875rem" />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Skeleton width="3.5rem" height="1.25rem" />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Skeleton width="2rem" height="2rem" radius="0.375rem" />
                  <Skeleton width="2rem" height="2rem" radius="0.375rem" />
                  <Skeleton width="2rem" height="2rem" radius="0.375rem" />
                </div>
              </div>
            </div>
          ))
        ) : history.length > 0 ? (
          history.map(tx => (
            <div key={tx.id} className="al-tx-row">

              {/* Left: icon + info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div className={`al-tx-icon al-tx-icon--${tx.type}`}>
                  {tx.type === 'sale'
                    ? <ShoppingCart style={{ width: '1.25rem', height: '1.25rem' }} />
                    : <RotateCcw style={{ width: '1.25rem', height: '1.25rem' }} />
                  }
                </div>
                <div>
                  <p className="al-tx-id">
                    {tx.invoiceId
                      ? `Factura ${tx.invoiceNumber || tx.invoiceId}`
                      : tx.ticketNumber ? `Ticket ${tx.ticketNumber}` : tx.id}
                  </p>
                  <p className="al-tx-meta">
                    {tx.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {' • '}
                    {PAYMENT_LABELS[tx.paymentMethod] || tx.paymentMethod}
                    {tx.invoiceId && tx.ticketNumber && tx.ticketNumber !== tx.invoiceNumber && ` • Ticket ${tx.ticketNumber}`}
                  </p>
                </div>
              </div>

              {/* Right: amount + actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <p className={`al-tx-amount al-tx-amount--${tx.type}`}>
                  {tx.type === 'return' ? '-' : ''}{formatCurrency(tx.total)}
                </p>

                {tx.type === 'sale' && (
                  <button
                    onClick={() => onReturn(tx)}
                    title="Devolución"
                    className="al-tx-action"
                    style={{ color: '#94A3B8' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#EF4444')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94A3B8')}
                  >
                    <RotateCcw style={{ width: '1.25rem', height: '1.25rem' }} />
                  </button>
                )}

                {/*
                  Una venta con factura se ve y se imprime como factura; sin
                  ella, como ticket. Nunca los dos a la vez.
                */}
                {tx.invoiceId ? (
                  <>
                    <button
                      onClick={() => onViewInvoice(tx)}
                      title="Ver Factura"
                      className="al-tx-action"
                      style={{ color: '#94A3B8' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#E07A5F')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#94A3B8')}
                    >
                      <FileSearch style={{ width: '1.25rem', height: '1.25rem' }} />
                    </button>

                    <button
                      onClick={() => onInvoice(tx)}
                      title="Imprimir Factura"
                      className="al-tx-action"
                      style={{ color: '#94A3B8' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#E07A5F')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#94A3B8')}
                    >
                      <FileText style={{ width: '1.25rem', height: '1.25rem' }} />
                    </button>
                  </>
                ) : (
                  <>
                    {tx.type === 'sale' && (
                      <button
                        onClick={() => onMakeInvoice(tx)}
                        title="Convertir en factura"
                        className="al-tx-action"
                        style={{ color: '#94A3B8' }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#E07A5F')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#94A3B8')}
                      >
                        <FilePlus style={{ width: '1.25rem', height: '1.25rem' }} />
                      </button>
                    )}

                    <button
                      onClick={() => onViewTicket(tx)}
                      title="Ver Ticket"
                      className="al-tx-action"
                      style={{ color: '#94A3B8' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#3D405B')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#94A3B8')}
                    >
                      <Eye style={{ width: '1.25rem', height: '1.25rem' }} />
                    </button>

                    <button
                      onClick={() => onReprint(tx)}
                      title="Imprimir Ticket"
                      className="al-tx-action"
                      style={{ color: '#94A3B8' }}
                      onMouseEnter={e => (e.currentTarget.style.color = '#3D405B')}
                      onMouseLeave={e => (e.currentTarget.style.color = '#94A3B8')}
                    >
                      <Ticket style={{ width: '1.25rem', height: '1.25rem' }} />
                    </button>
                  </>
                )}
              </div>

            </div>
          ))
        ) : (
          <div style={{ padding: '10rem 0', textAlign: 'center', color: '#D4D4D4', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <History style={{ width: '4rem', height: '4rem', opacity: 0.1 }} />
            <p style={{ fontStyle: 'italic' }}>No hay transacciones hoy.</p>
          </div>
        )}
      </div>

    </div>
  );
}